<template>
  <div class="PhoneVerification" data-e2e="phoneVerification">
    <div v-if="verified" class="PhoneVerification__verified" data-e2e="phoneVerified">
      <span class="PhoneVerification__mark">&#10003;</span>
      <span>{{ $t('phone_verification.verified') }}</span>
      <button type="button" class="PhoneVerification__link" data-e2e="phoneEdit" @click="$emit('edit')">
        {{ $t('phone_verification.edit') }}
      </button>
    </div>
    <template v-else>
      <div class="PhoneVerification__send">
        <LLButton
          :action="sendCode"
          :loading="sending"
          :disabled="!canSend"
          secondary
          small
          data-e2e="sendCodeBtn"
        >
          {{ sent ? $t('phone_verification.resend') : $t('phone_verification.send_code') }}
          <template v-if="countdown > 0">&nbsp;({{ countdown }})</template>
        </LLButton>
      </div>
      <div v-if="sent" class="PhoneVerification__code">
        <LLInputText
          ref="code"
          v-model="code"
          :placeholder="$t('phone_verification.code_placeholder')"
          :mask="codeMask"
          :errors="error ? [error] : []"
          :disabled="checking"
          inputmode="numeric"
          autocomplete="one-time-code"
          data-e2e="phoneCodeInput"
          @input="onCodeInput"
          @keyup.native.enter="checkCode"
        />
      </div>
      <div v-if="error && !sent" class="PhoneVerification__error" data-e2e="phoneVerificationError">
        {{ error }}
      </div>
    </template>
  </div>
</template>

<script>
import LLButton from '@/components/common/ui-components/LLButton'
import LLInputText from '@/components/common/LLInput/LLInputText'

const RESEND_SECONDS = 30
const CODE_LENGTH = 6
// The mask only keeps digits; it must not draw its own "_" placeholder in the empty field
const CODE_MASK = {
  mask: `9{1,${CODE_LENGTH}}`,
  autoUnmask: true,
  placeholder: '',
  showMaskOnFocus: false,
  showMaskOnHover: false
}

export default {
  name: 'PhoneVerification',
  components: { LLButton, LLInputText },
  props: {
    phone: { type: String, default: '' },
    studyId: { type: [Number, String], required: true },
    verified: { type: Boolean, default: false }
  },
  data() {
    return {
      sent: false,
      sentPhone: null,
      code: '',
      error: '',
      sending: false,
      checking: false,
      countdown: 0,
      timer: null
    }
  },
  computed: {
    codeMask() {
      return CODE_MASK
    },
    // The server decides what a valid number is; this only keeps the button off while nothing is typed
    hasPhone() {
      return /^\+\d{8,15}$/.test(this.phone || '')
    },
    canSend() {
      return this.hasPhone && !this.sending && this.countdown <= 0
    }
  },
  watch: {
    phone(value) {
      // 2.5: another number needs a new code
      if (this.sent && value !== this.sentPhone) this.clear()
      // An error under the phone field was about the number that was there before the edit
      else if (!this.sent) this.error = ''
    }
  },
  beforeDestroy() {
    clearInterval(this.timer)
  },
  methods: {
    clear() {
      this.sent = false
      this.sentPhone = null
      this.code = ''
      this.error = ''
      this.stopCountdown()
    },
    startCountdown(seconds = RESEND_SECONDS) {
      this.stopCountdown()
      this.countdown = seconds
      this.timer = setInterval(() => {
        this.countdown -= 1
        if (this.countdown <= 0) this.stopCountdown()
      }, 1000)
    },
    stopCountdown() {
      clearInterval(this.timer)
      this.timer = null
      this.countdown = 0
    },
    messageFor(e) {
      const key = e?.response?.data?.key
      const known = {
        PHONE_VERIFICATION_WRONG_CODE: 'phone_verification.wrong_code',
        PHONE_VERIFICATION_TOO_MANY_ATTEMPTS: 'phone_verification.too_many_attempts',
        PHONE_VERIFICATION_RATE_LIMITED: 'phone_verification.rate_limited',
        PHONE_NOT_US_CA_MOBILE: 'phone_verification.not_us_ca_mobile',
        PHONE_ALREADY_VERIFIED_ELSEWHERE: 'phone_verification.already_in_use',
        INCOMPLETE_PHONE_NUMBER: 'phone_verification.incomplete_phone',
        INVALID_PHONE_NUMBER: 'phone_verification.invalid_phone'
      }
      return known[key]
        ? this.$t(known[key])
        : e?.response?.data?.message || this.$t('phone_verification.generic_error')
    },
    async sendCode() {
      if (!this.canSend) return
      this.sending = true
      this.error = ''
      try {
        await this.$api.user.sendPhoneCode({
          studyId: this.studyId,
          phone: this.phone
        })
        this.sent = true
        this.sentPhone = this.phone
        this.code = ''
        this.startCountdown()
        this.$nextTick(() => this.$refs.code?.focus())
      } catch (e) {
        this.error = this.messageFor(e)
        // 4.5: beyond a limit the button stays off for a while
        if (e?.response?.data?.key === 'PHONE_VERIFICATION_RATE_LIMITED') this.startCountdown()
      } finally {
        this.sending = false
      }
    },
    onCodeInput() {
      this.code = this.code.replace(/\D/g, '').slice(0, CODE_LENGTH)
      // Resetting the field after a wrong code also fires `input`; the error must stay until the next digit
      if (this.code) this.error = ''
      if (this.code.length === CODE_LENGTH) this.checkCode()
    },
    async checkCode() {
      if (this.checking || this.code.length < CODE_LENGTH) return
      this.checking = true
      this.error = ''
      try {
        const { prospectAccount } = await this.$api.user.checkPhoneCode({
          studyId: this.studyId,
          phone: this.sentPhone,
          code: this.code
        })
        this.stopCountdown()
        this.$emit('verified', { phone: this.sentPhone, prospectAccount })
      } catch (e) {
        this.error = this.messageFor(e)
        this.code = ''
        // 2.8: after too many attempts a new code can be requested after 30 seconds
        if (e?.response?.data?.key === 'PHONE_VERIFICATION_TOO_MANY_ATTEMPTS') {
          this.sent = false
          this.startCountdown()
        }
      } finally {
        this.checking = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.PhoneVerification {
  @apply mt-2;

  &__verified {
    @apply flex items-center gap-2;
  }
  &__mark {
    @apply font-bold;
    color: #1a9e55;
  }
  &__link {
    @apply ml-2 underline;
  }
  &__send {
    @apply mb-2;
  }
  &__code {
    @apply mt-2;
  }
  &__error {
    @apply mt-2 text-sm;
    color: #d13b3b;
  }
}
</style>
